// Third-party packages
import {
	Collection,
} from '@@types/Collection';
import {
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';

// Templates / Layouts / Pages
import {
	OfferEntryJson,
} from '@layouts/Pages/PageOfferEntry';

export interface SearchAddressData {
	decoded?: string;
	id: number;
	is_valid?: boolean;
	street_raw: string;
}

export interface OfferEntryLocationState {
	isError: boolean;
	isLoading: boolean;
	searchAddress: SearchAddressData | null;
	suggestionOffers: Collection<OfferEntryJson, OfferEntryJson> | null;
	suggestionOffersError: boolean;
	suggestionOffersLoading: boolean;
}

export interface OffersEntryLocationStore {
	[offerId: number]: OfferEntryLocationState;
}

const initialState: OffersEntryLocationStore = {};

export const offersEntryLocationSlice = createSlice({
	initialState,
	name: 'offers_entry_location',
	reducers: {
		clearOfferEntryLocation: (state, action: PayloadAction<number>) => {
			const offerId = action.payload;
			delete state[offerId];
		},
		setOfferEntryLocation: (state, action: PayloadAction<{
			offerId: number;
			searchAddress: SearchAddressData | null;
		}>) => {
			const {
				offerId,
				searchAddress,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].searchAddress = searchAddress;
		},
		setOfferEntryLocationError: (state, action: PayloadAction<{
			isError: boolean;
			offerId: number;
		}>) => {
			const {
				isError,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].isError = isError;
		},
		setOfferEntryLocationLoading: (state, action: PayloadAction<{
			isLoading: boolean;
			offerId: number;
		}>) => {
			const {
				isLoading,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].isLoading = isLoading;
		},
		setOfferEntrySuggestionOffers: (state, action: PayloadAction<{
			offerId: number;
			suggestionOffers: Collection<OfferEntryJson, OfferEntryJson> | null;
		}>) => {
			const {
				offerId,
				suggestionOffers,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].suggestionOffers = suggestionOffers;
		},
		setOfferEntrySuggestionOffersError: (state, action: PayloadAction<{
			isError: boolean;
			offerId: number;
		}>) => {
			const {
				isError,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].suggestionOffersError = isError;
		},
		setOfferEntrySuggestionOffersLoading: (state, action: PayloadAction<{
			isLoading: boolean;
			offerId: number;
		}>) => {
			const {
				isLoading,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					searchAddress: null,
					suggestionOffers: null,
					suggestionOffersError: false,
					suggestionOffersLoading: false,
				};
			}
			state[offerId].suggestionOffersLoading = isLoading;
		},
	},
});

export const {
	clearOfferEntryLocation,
	setOfferEntryLocation,
	setOfferEntryLocationError,
	setOfferEntryLocationLoading,
	setOfferEntrySuggestionOffers,
	setOfferEntrySuggestionOffersError,
	setOfferEntrySuggestionOffersLoading,
} = offersEntryLocationSlice.actions;

export const offersEntryLocationReducer = offersEntryLocationSlice.reducer;
